import { useCallback, useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { atualizarUsuario, listarUsuarios } from '../../api/usuarios.js';
import type { UsuarioDoPainel } from '../../api/usuarios.js';
import { useSessao } from '../../autenticacao/SessaoContexto.js';
import type { PerfilUsuario } from '../../autenticacao/tipos.js';
import { PERFIS } from '../../autorizacao/permissoes.js';
import { usePode } from '../../autorizacao/usePode.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoBusca, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import { IconeMais, IconeUsuarios } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { FormularioDeSenha } from './FormularioDeSenha.js';
import { FormularioDeUsuario } from './FormularioDeUsuario.js';
import './AbaUsuariosDoPainel.css';

const TODOS = 'todos';

const OPCOES_DE_PERFIL = [
  { valor: TODOS, rotulo: 'Perfil · todos' },
  ...PERFIS.map((perfil) => ({ valor: perfil, rotulo: perfil })),
];

const OPCOES_DE_SITUACAO = [
  { valor: TODOS, rotulo: 'Situação · todas' },
  { valor: 'ativos', rotulo: 'Ativos' },
  { valor: 'inativos', rotulo: 'Inativos' },
];

type Painel =
  | { readonly tipo: 'novo' }
  | { readonly tipo: 'editar'; readonly usuario: UsuarioDoPainel }
  | { readonly tipo: 'senha'; readonly usuario: UsuarioDoPainel };

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

/**
 * Usuários do painel (UC13).
 *
 * Só quem pode `usuarios.gerenciar` enxerga cadastro, edição, senha e
 * desativação — a ação negada não vai para o DOM. A API recusa de qualquer
 * forma; a interface é a primeira das duas camadas.
 */
export function AbaUsuariosDoPainel() {
  const pode = usePode();
  const { estado: sessao } = useSessao();
  const idDeQuemEstaNaTela = sessao.situacao === 'autenticado' ? sessao.sessao.usuario.id : null;

  const [busca, definirBusca] = useState('');
  const [perfil, definirPerfil] = useState<string>(TODOS);
  const [situacao, definirSituacao] = useState<string>(TODOS);
  const [pagina, definirPagina] = useState(1);
  const [painel, definirPainel] = useState<Painel | null>(null);
  const [aDesativar, definirADesativar] = useState<UsuarioDoPainel | null>(null);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(
    () =>
      listarUsuarios({
        pagina,
        ...seInformado('busca', busca),
        ...seInformado('perfil', perfil === TODOS ? null : (perfil as PerfilUsuario)),
        ...seInformado('ativo', situacao === TODOS ? null : situacao === 'ativos'),
      }),
    [pagina, busca, perfil, situacao],
  );

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  /** Todo recorte novo volta à primeira página: a 7 do filtro velho não existe no novo. */
  function trocarFiltro(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  function concluir(texto: string) {
    definirPainel(null);
    definirAviso({ tom: 'ok', texto });
    recarregar();
  }

  function alternarAtivo(usuario: UsuarioDoPainel, ativo: boolean) {
    definirAgindo(true);
    definirErroDoDialogo(null);

    atualizarUsuario(usuario.id, { ativo })
      .then(() => {
        definirAgindo(false);
        definirADesativar(null);
        definirAviso({
          tom: 'ok',
          texto: ativo
            ? `${usuario.usuario} voltou a ter acesso ao painel.`
            : `${usuario.usuario} não entra mais no painel. As sessões dele foram encerradas.`,
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        const texto = mensagemDoErro(falha);

        if (ativo) {
          definirAviso({ tom: 'erro', texto });
        } else {
          definirErroDoDialogo(texto);
        }
      });
  }

  const gerencia = pode('usuarios.gerenciar');

  const colunas: readonly Coluna<UsuarioDoPainel>[] = [
    {
      chave: 'usuario',
      rotulo: 'Login',
      largura: '16%',
      celula: (usuario) => <span className="usuarios-painel__login">{usuario.usuario}</span>,
    },
    { chave: 'nome', rotulo: 'Nome', largura: '22%', celula: (usuario) => usuario.nome },
    { chave: 'perfil', rotulo: 'Perfil', largura: '14%', celula: (usuario) => usuario.perfil },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      largura: '12%',
      celula: (usuario) =>
        usuario.ativo ? (
          <ChipStatus tom="concluida" rotulo="Ativo" tamanho="tabela" />
        ) : (
          <ChipStatus tom="desativado" rotulo="Inativo" tamanho="tabela" />
        ),
    },
    {
      chave: 'acesso',
      rotulo: 'Último acesso',
      largura: '16%',
      celula: (usuario) => (
        <span className="usuarios-painel__discreto">
          {formatarDataHora(usuario.ultimoAcessoEm, 'Nunca acessou')}
        </span>
      ),
    },
    {
      chave: 'acoes',
      rotulo: '',
      alinhamento: 'direita',
      celula: (usuario) => {
        if (!gerencia) {
          return null;
        }

        const ehProprio = usuario.id === idDeQuemEstaNaTela;

        return (
          <div className="usuarios-painel__acoes">
            <Botao
              altura="pequena"
              aria-label={`Editar ${usuario.usuario}`}
              onClick={() => {
                definirPainel({ tipo: 'editar', usuario });
              }}
            >
              Editar
            </Botao>
            <Botao
              altura="pequena"
              aria-label={`Alterar senha de ${usuario.usuario}`}
              onClick={() => {
                definirPainel({ tipo: 'senha', usuario });
              }}
            >
              Senha
            </Botao>
            {!ehProprio &&
              (usuario.ativo ? (
                <Botao
                  altura="pequena"
                  variante="destrutivo"
                  aria-label={`Desativar ${usuario.usuario}`}
                  onClick={() => {
                    definirErroDoDialogo(null);
                    definirADesativar(usuario);
                  }}
                >
                  Desativar
                </Botao>
              ) : (
                <Botao
                  altura="pequena"
                  variante="sucesso"
                  aria-label={`Ativar ${usuario.usuario}`}
                  onClick={() => {
                    alternarAtivo(usuario, true);
                  }}
                >
                  Ativar
                </Botao>
              ))}
          </div>
        );
      },
    },
  ];

  return (
    <div className="usuarios-painel">
      <FaixaDeFiltros rotulo="Filtros de usuários do painel">
        <CampoBusca
          rotulo="Buscar por nome ou login"
          valor={busca}
          largura={260}
          aoMudar={(valor) => {
            trocarFiltro(() => {
              definirBusca(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Filtrar por perfil"
          valor={perfil}
          opcoes={OPCOES_DE_PERFIL}
          aoMudar={(valor) => {
            trocarFiltro(() => {
              definirPerfil(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Filtrar por situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO}
          aoMudar={(valor) => {
            trocarFiltro(() => {
              definirSituacao(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'usuário', 'usuários')}
          </ContadorRegistros>
        )}
        {gerencia && (
          <span className="usuarios-painel__acao-principal">
            <Botao
              variante="primario"
              altura="media"
              onClick={() => {
                definirPainel({ tipo: 'novo' });
              }}
            >
              <IconeMais tamanho={15} />
              Novo usuário
            </Botao>
          </span>
        )}
      </FaixaDeFiltros>

      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando usuários do painel…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeUsuarios tamanho={22} />}
            titulo="Nenhum usuário do painel neste recorte"
            detalhe="Ajuste a busca, o perfil ou a situação para encontrar quem procura."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(usuario) => usuario.id}
            descricao="Usuários do painel"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'usuário', 'usuários')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>

      {painel?.tipo === 'novo' && (
        <FormularioDeUsuario
          usuario={null}
          ehProprio={false}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'editar' && (
        <FormularioDeUsuario
          usuario={painel.usuario}
          ehProprio={painel.usuario.id === idDeQuemEstaNaTela}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'senha' && (
        <FormularioDeSenha
          usuario={painel.usuario}
          ehProprio={painel.usuario.id === idDeQuemEstaNaTela}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {aDesativar !== null && (
        <DialogoConfirmacao
          titulo={`Desativar o acesso de ${aDesativar.usuario}?`}
          rotuloConfirmar="Desativar acesso"
          agindo={agindo}
          aoConfirmar={() => {
            alternarAtivo(aDesativar, false);
          }}
          aoCancelar={() => {
            definirADesativar(null);
            definirErroDoDialogo(null);
          }}
        >
          <p>
            {aDesativar.nome} deixa de entrar no painel, e as sessões abertas dele são encerradas na
            hora.
          </p>
          <p>
            O registro não é apagado: a trilha de auditoria continua apontando para ele como autor
            do que já aconteceu.
          </p>
          {erroDoDialogo !== null && <p className="usuarios-painel__erro">{erroDoDialogo}</p>}
        </DialogoConfirmacao>
      )}
    </div>
  );
}
